'use client';

import Image from 'next/image';
import {
  Button,
  Checkbox,
  InfoCard,
  Input,
  Select,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '@goodneighbor/ui';
import {
  useKindnessChallengeForm,
  useKindnessChallengeFormKey,
  type KindnessChallengeSuccessData
} from './useKindnessChallengeForm';
import styles from './kindnessChallenge.module.scss';
import { PageName } from '@goodneighbor/analytics';
import { useAnalyticsPageView } from '@/hooks/useAnalyticsPageView';

export default function KindnessChallengeForm() {
  useAnalyticsPageView(PageName.KindnessChallengeForm);

  const { formKey, successData, handleSuccess } = useKindnessChallengeFormKey();

  return (
    <KindnessChallengeFormInner
      key={formKey}
      onSuccess={handleSuccess}
      successData={successData}
    />
  );
}

function KindnessChallengeFormInner({
  onSuccess,
  successData
}: {
  onSuccess: (data: KindnessChallengeSuccessData) => void;
  successData: KindnessChallengeSuccessData | null;
}) {
  const { form, mutation, onSubmit } = useKindnessChallengeForm(onSuccess);

  if (successData) {
    return (
      <div className={styles.successScreen}>
        <Image
          src="/placeholder/celebration.svg"
          alt=""
          width={180}
          height={173}
          aria-hidden
          className={styles.successIcon}
        />
        <h2 className={styles.successTitle}>You&apos;re in!</h2>
        <p className={styles.successSubtitle}>
          Thank you {successData.firstName} for your entry for the Kindness
          Challenge. Your <br />
          community is brighter because of you.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.header}>
        <h1 className={styles.title}>Sign up for your Kindness Challenge</h1>
        <p className={styles.subtitle}>
          Fill out the form below for a chance to win exclusive prizes.
        </p>
      </div>
      <Form {...form}>
        <form className={styles.form} onSubmit={form.handleSubmit(onSubmit)}>
          <div className={styles.fieldRow}>
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className={styles.formItem}>
                  <FormControl>
                    <Input
                      {...field}
                      label="First name"
                      placeholder="First name"
                      hideIcon
                      isRequired
                      labelClassName={styles.label}
                      wrapperClassName={styles.inputWrapper}
                      inputClassName={styles.inputField}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className={styles.formItem}>
                  <FormControl>
                    <Input
                      {...field}
                      label="Last name"
                      placeholder="Last name"
                      hideIcon
                      isRequired
                      labelClassName={styles.label}
                      wrapperClassName={styles.inputWrapper}
                      inputClassName={styles.inputField}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className={styles.fieldRow}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className={styles.formItem}>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      label="Email"
                      placeholder="you@example.com"
                      hideIcon
                      isRequired
                      labelClassName={styles.label}
                      wrapperClassName={styles.inputWrapper}
                      inputClassName={styles.inputField}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem className={styles.formItem}>
                  <FormControl>
                    <Input
                      type="tel"
                      {...field}
                      label="Phone number"
                      placeholder="+1 (555) 123-4567"
                      hideIcon
                      hideCountrySelector
                      isRequired
                      labelClassName={styles.label}
                      wrapperClassName={styles.inputWrapper}
                      inputClassName={styles.inputField}
                    />
                  </FormControl>
                  <FormMessage />
                  <p className={styles.phoneHint}>
                    We will call you if you win!
                  </p>
                </FormItem>
              )}
            />
          </div>

          <div className={styles.fieldRow}>
            <FormField
              control={form.control}
              name="socialHandle"
              render={({ field }) => (
                <FormItem className={styles.formItem}>
                  <FormControl>
                    <Input
                      {...field}
                      label="Social handle"
                      placeholder="@yourhandle"
                      hideIcon
                      isRequired
                      labelClassName={styles.label}
                      wrapperClassName={styles.inputWrapper}
                      inputClassName={styles.inputField}
                    />
                  </FormControl>
                  <FormMessage />
                  <p className={styles.phoneHint}>
                    We will use this to validate your entry.
                  </p>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="socialPlatform"
              render={({ field }) => (
                <FormItem className={styles.formItem}>
                  <FormControl>
                    <Select
                      label="Social platform"
                      isRequired
                      items={[
                        { id: 'instagram', label: 'Instagram' },
                        { id: 'tiktok', label: 'TikTok' },
                        { id: 'facebook', label: 'Facebook' }
                      ]}
                      selectedKey={field.value}
                      onSelectionChange={(key) => field.onChange(key as string)}
                      labelClassName={styles.label}
                    >
                      {(item) => (
                        <Select.Item id={item.id}>{item.label}</Select.Item>
                      )}
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="termsAccepted"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Checkbox
                    isSelected={field.value === true}
                    onChange={(checked) =>
                      field.onChange(checked ? true : undefined)
                    }
                    size="sm"
                    label={
                      <span className={styles.termsText}>
                        I confirm that I am over the age of 18 and this is my
                        participation and agree to the official contest rules.
                        To be an eligible winner, I understand that entry
                        validation will be completed at time of draw.{' '}
                        <a
                          href="https://goodneighbordayamerica.com/kindness-challenge/terms-and-conditions"
                          className={styles.termsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Terms and Conditions
                        </a>
                      </span>
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {mutation.isError && (
            <InfoCard severity="error">
              {(mutation.error as { message: string })?.message ??
                'Something went wrong. Please try again.'}
            </InfoCard>
          )}

          <Button
            className={styles.submitButton}
            type="submit"
            size="lg"
            isLoading={mutation.isPending}
          >
            Enter to win
          </Button>
        </form>
      </Form>
    </>
  );
}
